'use strict';

const SvgModel = require("./models/SvgModel");
const SvgModelActions = require('./const/SvgModelActions.json');
const axios = require("axios");
const imageSize = require('image-size');
const customsCloudLibrary = require('@vfdp-customs/customs-cloud-library');
/**
 * SVG Service
 */
class SvgService {

  /**
   * Constructor
   * @param configs Configs from Secrets
   * @param getSwatch Get Swatch Function
   */
  constructor(configs, getSwatch = false) {
    const {origin, assetsBucket, swatchesBucket, httpConfig, cloudProviderName, s32DImagesBucket} = configs;

    if (origin) {
      this.assetsBaseUrl = `https://${origin}/${assetsBucket}`;
      this.swatchesBaseUrl = `https://${origin}/${swatchesBucket}/assets`;
    } else {
      this.assetsBaseUrl = `/${assetsBucket}`;
      this.swatchesBaseUrl = `/${swatchesBucket}/assets`;
    }
    this.assetsBucket = assetsBucket;
    this.swatchesBucket = swatchesBucket;
    this.httpOptions = httpConfig;
    this.graphicsPath = '/assets/svg/graphics';
    this.getSwatch = getSwatch;

    this.s32DImagesBucket = s32DImagesBucket;
    this.objManagement = customsCloudLibrary.ObjectManagement({
      cloudProviderName: cloudProviderName
    });
    this.buckets = [ this.s32DImagesBucket, this.swatchesBucket, this.assetsBucket];
  }

  /**
   * Is SVG Component
   *
   * @param component Component
   * @returns {boolean}
   */
  static isSvgComponent(component) {
    // TODO: determine where if vue code this group is getting improperly set.  it should always be `graphics`
    return component.group
      && (component.group === 'graphics' || component.group === 'graphic')
      && component.swatch_id !== null;
  }

  async getFromBucket(Bucket, Key, isBufferResponse = false) {
    try{
      const contentFile = await this.objManagement.getObject({ Bucket, Key });
      if(!contentFile) throw new Error(`Access denied to the file ${Key}`);
      if(isBufferResponse) return contentFile.Body;
      return contentFile.Body.toString('utf-8');
    } catch(err){
      throw err;
    }
  }

  /**
   * Get Image Data From Bucket
   *
   * @param url Image URL
   * @returns {Promise<Object>}
   */
  async getImageDataFromBucket(url) {
    const bucket = this.buckets.find((bucket) => url.indexOf(bucket) > -1);
    if(!bucket) throw new Error('Bucket not found');
    const key = url.substring(url.indexOf(bucket) + bucket.length + 1, url.length);
    const data = await this.getFromBucket(bucket, key, true);
    const binaryBuffer = Buffer.from(data, 'binary');
    const base64 = binaryBuffer.toString('base64');
    const dimensions = imageSize(binaryBuffer);

    return {
      href: `data:image/${dimensions.type};base64,${base64}`,
      width: dimensions.width,
      height: dimensions.height
    }
  }

  /**
   * Build Recipe SVGs
   * @param recipe Recipe
   * @param data Additional Data
   * @returns {Promise<*[]>}
   */
  async buildRecipeSvgs(recipe, data) {
    const svgModels = []

    for (const componentName in recipe.components) {
      const component = recipe.components[componentName];

      const svgModel = await this.buildSvg(component, data);

      if (svgModel !== null) {
        svgModels[componentName] = svgModel;
      }
    }

    return svgModels;
  }

  /**
   * Build SVG
   *
   * @param component Component
   * @param data Additional Data
   * @returns {Promise<null|SvgModel>}
   */
  async buildSvg(component, data) {
    if (!SvgService.isSvgComponent(component)) {
      return null;
    }

    const svgModel = await this.newSvg(component.swatch_id);

    if (data && data.additionalStyles) {
      svgModel.setStyles(data.additionalStyles);
    }

    for (const [component_name, subComponent] of Object.entries(component.sub_components)) {
      await this.updateComponent(svgModel, component_name, subComponent, data);
    }

    return svgModel;
  }

  /**
   * New SVG Model
   *
   * @param swatch_id Swatch ID
   * @returns {Promise<SvgModel>}
   */
  async newSvg(swatch_id) {
    const svgModel = new SvgModel();

    await this.updateDesign(svgModel, swatch_id);

    return svgModel;
  }


  /**
   * Update Design
   *
   * @param svgModel SVG Model
   * @param swatch_id Swatch ID
   * @returns {Promise<AxiosResponse<*>>}
   */
  async updateDesign(svgModel, swatch_id) {
    try{
    const svgContent = await this.getFromBucket(this.assetsBucket, `assets/svg/graphics/${swatch_id}.svg`);
    if(!svgContent) throw new Error(`Svg not found assets/svg/graphics/${swatch_id}.svg`);
    return svgModel.updateSvg(svgContent);
    } catch(err) {
      throw err;
    }
  }

  /**
   * Update Component
   *
   * @param svgModel SVG Model
   * @param component_name Component Name
   * @param component Component
   * @param data Additional Data
   */
  async updateComponent(svgModel, component_name, component, data) {
    let action;
    let options = {};

    // TODO: determine where if vue code this group is getting improperly set.  It should always be `solids`
    if ((component.group === 'solids' || component.group === 'solids-apparel') && component.toolkit_options && component.toolkit_options.hex) {
      action = SvgModelActions.FILL;
    }

    if (component.group === 'patterns') {
      action = SvgModelActions.PATTERN;

      if (!(component.pattern_data && component.pattern_data.href)) {
        const swatch = await this.getSwatch(component.swatch_id);
        const image_url = (swatch.image_assets && swatch.image_assets.svg_map)
          || (swatch.material_options && swatch.material_options.textures && swatch.material_options.textures.map
            && swatch.material_options.textures.map.image_url)
        options.pattern_data = await this.getImageDataFromBucket(this.swatchesBaseUrl + image_url);
        options.pattern_data = await this.getImageDataFromBucket(this.swatchesBaseUrl + image_url);

        if (data && data.placementPosition && data.dpi) {
          const sizeMultiplier = svgModel.getWidth() / data.placementPosition.width * 72 / data.dpi;
          options.pattern_data.width = options.pattern_data.width * sizeMultiplier;
          options.pattern_data.height = options.pattern_data.height * sizeMultiplier;
        }
      }
    }

    if (component.group === 'ugc' && component.toolkit_options && component.toolkit_options.viewBox) {
      action = SvgModelActions.UGC;

      if (!(component.ugc_data && component.ugc_data.href)) {
        options.ugc_data = await this.getImageDataFromBucket(component.ugc.original_image.image_url);
      }
    }

    if (action) {
      svgModel.updateComponent({action, component_name, component, options});
    }
  }

  /**
   * Update SVG Model Viewport
   *
   * @param svgModel
   * @param x
   * @param y
   * @param w
   * @param h
   * @param setViewBox boolean to update viewbox
   */
  updateViewport(svgModel, x, y, w, h, setViewBox = false) {
    svgModel
      .setX(x)
      .setY(y)
      .setWidth(w)
      .setHeight(h)

    if (setViewBox) svgModel.setViewbox(0, 0, w, h)
  }
}

module.exports = SvgService;
